import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import {
  MessageFeedbackInput,
  submitMessageFeedback,
} from "apps/user/learner/api/coach-feedback";
import {
  FeedbackState,
  FeedbackVote,
} from "apps/user/learner/pages/chat/types";

interface UseMessageFeedbackOptions {
  messageId: string;
  /** Server-hydrated feedback, seeding local state for this message. */
  initialFeedback?: FeedbackState | null;
}

interface UseMessageFeedbackReturn {
  feedback: FeedbackState | null;
  isSubmitting: boolean;
  submitVote: (vote: FeedbackVote) => Promise<void>;
  submitComment: (text: string) => Promise<void>;
  deleteComment: () => Promise<void>;
}

// A record with neither a vote nor a comment is no record at all — what Coach's 204 means.
function normalise(state: FeedbackState): FeedbackState | null {
  return state.vote === null && !state.has_text ? null : state;
}

export default function useMessageFeedback({
  messageId,
  initialFeedback = null,
}: UseMessageFeedbackOptions): UseMessageFeedbackReturn {
  const { t } = useTranslation(["ori"]);
  const { openToast } = useToast();
  const [feedback, setFeedback] = useState<FeedbackState | null>(
    initialFeedback
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State lags the commit, so two fast clicks would both pass a state-only guard.
  const inFlight = useRef(false);
  // Re-seed only when the row moves to another message; a later render of the
  // same message must not overwrite what the learner just submitted.
  const seededFor = useRef(messageId);

  useEffect(() => {
    if (seededFor.current !== messageId) {
      seededFor.current = messageId;
      setFeedback(initialFeedback);
    }
  }, [messageId, initialFeedback]);

  const submit = useCallback(
    async (
      input: MessageFeedbackInput,
      optimistic: FeedbackState,
      onSuccess: () => void,
      errorTitle: string
    ) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const previous = feedback;
      setFeedback(normalise(optimistic));
      setIsSubmitting(true);
      try {
        setFeedback(normalise(await submitMessageFeedback(messageId, input)));
        onSuccess();
      } catch (error) {
        logError("Ori message feedback submission failed", error);
        setFeedback(previous);
        openToast({
          messageHeader: errorTitle,
          message: t("ori:chat.feedback.toast.errorBody"),
          variant: "error",
        });
      } finally {
        inFlight.current = false;
        setIsSubmitting(false);
      }
    },
    [feedback, messageId, openToast, t]
  );

  const submitVote = useCallback(
    async (vote: FeedbackVote) => {
      await submit(
        { vote },
        {
          vote,
          has_text: feedback?.has_text ?? false,
          feedback_text: feedback?.feedback_text,
        },
        () =>
          vote === null
            ? trackEvent("Coach Message Feedback Vote Removed", {
                message_id: messageId,
              })
            : trackEvent("Coach Message Feedback Vote Submitted", {
                Vote: vote,
                message_id: messageId,
              }),
        t("ori:chat.feedback.toast.voteErrorTitle")
      );
    },
    [feedback, messageId, submit, t]
  );

  const submitComment = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      await submit(
        { feedbackText: text },
        { vote: feedback?.vote ?? null, has_text: true, feedback_text: text },
        () => {
          trackEvent("Coach Message Feedback Submitted", {
            message_id: messageId,
          });
          openToast({
            messageHeader: t("ori:chat.feedback.toast.savedTitle"),
            message: t("ori:chat.feedback.toast.savedBody"),
            variant: "success",
          });
        },
        t("ori:chat.feedback.toast.commentErrorTitle")
      );
    },
    [feedback, messageId, openToast, submit, t]
  );

  // Clearing the comment keeps any vote; Coach deletes the record only when nothing is left.
  const deleteComment = useCallback(async () => {
    await submit(
      { feedbackText: "" },
      { vote: feedback?.vote ?? null, has_text: false },
      () => {
        trackEvent("Coach Message Feedback Deleted", { message_id: messageId });
        openToast({ message: t("ori:chat.feedback.toast.deleted") });
      },
      t("ori:chat.feedback.toast.commentErrorTitle")
    );
  }, [feedback, messageId, openToast, submit, t]);

  return { feedback, isSubmitting, submitVote, submitComment, deleteComment };
}
